import { useCallback, useEffect, useRef, useState } from 'react';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';

interface UsePaginatedSelectConfig<T> {
	queryHook: (params: {
		page: number;
		limit: number;
		search: string;
		[key: string]: any;
	}) => {
		data?: { items: T[]; meta?: any };
		isFetching: boolean;
		isError: boolean;
		error: any;
	};
	queryParams?: Record<string, any>;
	transform: (item: T) => SelectItem;
	errorMessage: string;
	resetKey?: string | boolean;
}

export const usePaginatedSelect = <T>({
	queryHook,
	queryParams = {},
	transform,
	errorMessage,
	resetKey,
}: UsePaginatedSelectConfig<T>) => {
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState('');
	const [allItems, setAllItems] = useState<SelectItem[]>([]);
	const [rawData, setRawData] = useState<T[]>([]); // Store raw data alongside transformed items
	const transformRef = useRef(transform);
	const previousSearchRef = useRef<string>('');
	const isInitialLoadRef = useRef<boolean>(true);

	// Keep latest transform without causing re-renders
	useEffect(() => {
		transformRef.current = transform;
	}, [transform]);

	// Reset when resetKey changes
	useEffect(() => {
		setAllItems([]);
		setRawData([]);
		setPage(1);
		setSearch('');
		previousSearchRef.current = '';
		isInitialLoadRef.current = true;
	}, [resetKey]);

	// Call the query hook with current page/search
	const { data, isFetching, isError, error } = queryHook({
		page,
		limit: 50,
		search,
		...queryParams,
	});

	// Accumulate items and raw data
	useEffect(() => {
		if (data?.items) {
			const newItems = data.items.map(transformRef.current);
			// Check if search actually changed (not just page reset)
			const searchChanged = search !== previousSearchRef.current;
			// Only reset if: (page is 1 AND search changed) OR (page is 1 AND it's the initial load with no items)
			const shouldReset = (page === 1 && searchChanged) || (page === 1 && isInitialLoadRef.current && allItems.length === 0);

			// Update refs before state updates
			if (shouldReset) {
				if (isInitialLoadRef.current) {
					isInitialLoadRef.current = false;
				}
				if (searchChanged) {
					previousSearchRef.current = search;
				}
			}

			setAllItems((prev) => {
				if (shouldReset) {
					return newItems;
				}
				const existingIds = new Set(prev.map((item) => item.value));
				const uniqueNew = newItems.filter(
					(item) => !existingIds.has(item.value),
				);
				return [...prev, ...uniqueNew];
			});

			// Also store raw data
			setRawData((prev) => {
				if (shouldReset) {
					return data.items;
				}
				const existingIds = new Set(
					prev.map((item) => {
						const transformed = transformRef.current(item);
						return transformed.value;
					}),
				);
				const uniqueNewRaw = data.items.filter((item) => {
					const transformed = transformRef.current(item);
					return !existingIds.has(transformed.value);
				});
				return [...prev, ...uniqueNewRaw];
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, page, search]);

	// Handle load more
	const handleLoadMore = useCallback(
		(newPage: number, _limit: number, newSearch: string) => {
			setPage(newPage);
			setSearch(newSearch);
		},
		[],
	);

	// Get error message
	const errorMsg = isError ? (error as Error)?.message || errorMessage : '';

	return {
		items: allItems,
		rawData, // Expose raw data for consumers who need it
		loading: isFetching,
		error: errorMsg,
		onLoadMore: handleLoadMore,
		search, // Expose search text for consumers who need it
	};
};
